import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Anchor,
    Badge,
    Button,
    Card,
    Center,
    Group,
    Loader,
    Modal,
    PasswordInput,
    Pagination,
    Select,
    SimpleGrid,
    Stack,
    Switch,
    Table,
    Text,
    TextInput,
    Textarea,
    Title,
} from '@mantine/core';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import * as XLSX from 'xlsx';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

interface UserRecord {
    id: number;
    email: string;
    first_name?: string;
    last_name?: string;
    dni?: string;
    user_type: string;
    is_approved: boolean;
    rejected_at?: string | null;
    phone_number?: string;
    applications_last_6m?: number | null;
    posted_jobs_last_6m?: number | null;
    applications_total?: number | null;
    posted_jobs_total?: number | null;
    bio?: string;
    skills?: string[];
    career?: string;
    company_name?: string;
    company_website?: string;
    profile_picture?: string | null;
    resume?: string | null;
    constancia_document?: string | null;
}

interface UserFormState {
    email: string;
    first_name: string;
    last_name: string;
    dni: string;
    user_type: string;
    is_approved: boolean;
    phone_number: string;
    bio: string;
    skills: string;
    company_name: string;
    company_website: string;
}

const UsersPage: React.FC = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [users, setUsers] = useState<UserRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [page, setPage] = useState(1);
    const [roleFilter, setRoleFilter] = useState('ALL');
    const [approvalFilter, setApprovalFilter] = useState('ALL');
    const [searchTerm, setSearchTerm] = useState('');
    const [resetPassword, setResetPassword] = useState('');
    const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
    const [editingUser, setEditingUser] = useState<UserRecord | null>(null);
    const [form, setForm] = useState<UserFormState>({
        email: '',
        first_name: '',
        last_name: '',
        dni: '',
        user_type: 'JOB_SEEKER',
        is_approved: false,
        phone_number: '',
        bio: '',
        skills: '',
        company_name: '',
        company_website: '',
    });

    useEffect(() => {
        loadUsers();
    }, []);

    const fetchAllUsersRaw = async () => {
        let currentPage = 1;
        let allUsers: UserRecord[] = [];

        while (true) {
            const response = await api.getUsers({ page: String(currentPage) });
            if (Array.isArray(response)) {
                allUsers = response as UserRecord[];
                break;
            }

            const pageResults = (response as any).results || [];
            allUsers = allUsers.concat(pageResults);

            if (!(response as any).next) {
                break;
            }
            currentPage += 1;
        }

        return allUsers;
    };

    const loadUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const usersData = await fetchAllUsersRaw();
            setUsers(usersData);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('users.loadError'));
        } finally {
            setLoading(false);
        }
    };

    const formatRole = (role: string) => {
        if (role === 'JOB_SEEKER') return t('common.roles.jobSeeker');
        if (role === 'JOB_POSTER') return t('common.roles.jobPoster');
        if (role === 'MODERATOR') return t('common.roles.moderator');
        if (role === 'ADMIN') return t('common.roles.admin');
        return role;
    };

    const applyFilters = (list: UserRecord[]) => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        return list.filter((u) => {
            const roleMatch = roleFilter === 'ALL' || u.user_type === roleFilter;
            const approvalMatch =
                approvalFilter === 'ALL' ||
                (approvalFilter === 'APPROVED'
                    ? u.is_approved
                    : approvalFilter === 'REJECTED'
                        ? Boolean(u.rejected_at)
                        : !u.is_approved && !u.rejected_at);
            const searchMatch =
                !normalizedSearch ||
                u.email?.toLowerCase().includes(normalizedSearch) ||
                u.first_name?.toLowerCase().includes(normalizedSearch) ||
                u.last_name?.toLowerCase().includes(normalizedSearch) ||
                u.dni?.toLowerCase().includes(normalizedSearch);
            return roleMatch && approvalMatch && searchMatch;
        });
    };

    const pageSize = 20;

    const filteredUsers = useMemo(
        () => applyFilters(users),
        [users, roleFilter, approvalFilter, searchTerm]
    );

    const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));

    const pagedUsers = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredUsers.slice(start, start + pageSize);
    }, [filteredUsers, page, pageSize]);

    useEffect(() => {
        setPage(1);
    }, [roleFilter, approvalFilter, searchTerm]);

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    const openEdit = async (record: UserRecord) => {
        setEditingUser(record);
        setSuccess(false);
        setError(null);
        setResetPassword('');
        setResetPasswordConfirm('');
        setForm({
            email: record.email || '',
            first_name: record.first_name || '',
            last_name: record.last_name || '',
            dni: record.dni || '',
            user_type: record.user_type || 'JOB_SEEKER',
            is_approved: Boolean(record.is_approved),
            phone_number: record.phone_number || '',
            bio: record.bio || '',
            skills: (record.skills || []).join(', '),
            company_name: record.company_name || '',
            company_website: record.company_website || '',
        });

        try {
            const detail = await api.getUser(record.id);
            setEditingUser(detail as UserRecord);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('users.loadError'));
        }
    };

    const formatStatus = (record: UserRecord) => {
        if (record.is_approved) return t('users.filters.approved');
        if (record.rejected_at) return t('users.filters.rejected');
        return t('users.filters.pending');
    };

    const careerAbbreviations: Record<string, string> = {
        MUSICOTERAPIA: 'MT',
        PSICOLOGIA: 'PSI',
        PROFESORADO_PSICOLOGIA: 'PROF',
        TERAPIA_OCUPACIONAL: 'TO',
    };

    const getUserExtra = (record: UserRecord) => {
        if (record.user_type === 'JOB_SEEKER') {
            if (!record.career) return '—';
            return careerAbbreviations[record.career] || record.career;
        }
        if (record.user_type === 'JOB_POSTER') return record.company_name || '—';
        return '—';
    };

    const buildExportRows = (records: UserRecord[]) =>
        records.map((record) => {
            const isSeeker = record.user_type === 'JOB_SEEKER';
            const isPoster = record.user_type === 'JOB_POSTER';
            const activityTotal = isSeeker
                ? record.applications_total ?? 0
                : isPoster
                    ? record.posted_jobs_total ?? 0
                    : '';
            const activity6m = isSeeker
                ? record.applications_last_6m ?? 0
                : isPoster
                    ? record.posted_jobs_last_6m ?? 0
                    : '';
            return {
                [t('users.export.email')]: record.email,
                [t('users.export.phone')]: record.phone_number || '',
                [t('users.export.firstName')]: record.first_name || '',
                [t('users.export.lastName')]: record.last_name || '',
                [t('users.export.activity6m')]: activity6m,
                [t('users.export.activityTotal')]: activityTotal,
                [t('approvals.career')]: record.career || '',
                [t('users.fields.companyName')]: record.company_name || '',
                [t('users.export.role')]: formatRole(record.user_type),
                [t('users.export.status')]: formatStatus(record),
            };
        });

    const buildExportDate = () => {
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const downloadBlob = (blob: Blob, filename: string) => {
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    const fetchAllUsersForExport = async () => {
        const allUsers = await fetchAllUsersRaw();
        return applyFilters(allUsers);
    };

    const escapeCsv = (value: string) => {
        const needsEscape = /[",\n]/.test(value);
        if (!needsEscape) return value;
        return `"${value.replace(/"/g, '""')}"`;
    };

    const handleExportCsv = async () => {
        try {
            setExporting(true);
            const rows = buildExportRows(await fetchAllUsersForExport());
            const headers = rows.length ? Object.keys(rows[0]) : [
                t('users.export.email'),
                t('users.export.phone'),
                t('users.export.firstName'),
                t('users.export.lastName'),
                t('users.export.activity6m'),
                t('users.export.activityTotal'),
                t('approvals.career'),
                t('users.fields.companyName'),
                t('users.export.role'),
                t('users.export.status'),
            ];
            const csvLines = [
                headers.join(','),
                ...rows.map((row) =>
                    headers
                        .map((header) => escapeCsv(String((row as Record<string, string>)[header] ?? '')))
                        .join(',')
                ),
            ];
            const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
            downloadBlob(blob, `users-export-${buildExportDate()}.csv`);
        } finally {
            setExporting(false);
        }
    };

    const handleExportXlsx = async () => {
        try {
            setExporting(true);
            const rows = buildExportRows(await fetchAllUsersForExport());
            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');
            const data = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            downloadBlob(blob, `users-export-${buildExportDate()}.xlsx`);
        } finally {
            setExporting(false);
        }
    };

    const closeEdit = () => {
        setEditingUser(null);
        setSuccess(false);
        setError(null);
        setResetPassword('');
        setResetPasswordConfirm('');
    };

    const handleSave = async () => {
        if (!editingUser) return;
        try {
            setSaving(true);
            setError(null);
            const skillsArray = form.skills
                .split(',')
                .map((skill) => skill.trim())
                .filter((skill) => skill.length > 0);

            if (resetPassword || resetPasswordConfirm) {
                if (resetPassword.length < 8) {
                    setError(t('users.password.tooShort'));
                    setSaving(false);
                    return;
                }
                if (resetPassword !== resetPasswordConfirm) {
                    setError(t('users.password.mismatch'));
                    setSaving(false);
                    return;
                }
            }

            const trimmedDni = form.dni.trim();
            if (trimmedDni && !/^\d{6,8}$/.test(trimmedDni)) {
                setError(t('register.dniInvalid'));
                setSaving(false);
                return;
            }

            const trimmedPhone = form.phone_number.trim();
            if (trimmedPhone && !/^\d{10,}$/.test(trimmedPhone)) {
                setError(t('register.phoneInvalid'));
                setSaving(false);
                return;
            }

            const payload = {
                email: form.email,
                first_name: form.first_name,
                last_name: form.last_name,
                dni: trimmedDni,
                user_type: form.user_type,
                is_approved: form.is_approved,
                phone_number: trimmedPhone,
                bio: form.bio,
                skills: skillsArray,
                company_name: form.user_type === 'JOB_POSTER' ? form.company_name : undefined,
                company_website: form.user_type === 'JOB_POSTER' ? form.company_website : undefined,
            };

            const updated = await api.updateUser(editingUser.id, payload);
            setUsers((prev) =>
                prev.map((item) => (item.id === editingUser.id ? { ...item, ...updated } : item))
            );

            if (resetPassword) {
                await api.resetUserPassword(editingUser.id, {
                    new_password: resetPassword,
                    new_password_confirm: resetPasswordConfirm,
                });
            }

            setSuccess(true);
            setTimeout(() => {
                closeEdit();
            }, 800);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('users.updateError'));
        } finally {
            setSaving(false);
        }
    };

    if (user && !['ADMIN', 'MODERATOR'].includes(user.user_type)) {
        return (
            <Card shadow="sm" padding="xl" radius="lg" withBorder>
                <Alert icon={<IconAlertCircle size={16} />} title={t('common.accessRestricted')} color="red" radius="md">
                    {t('common.accessDenied')}
                </Alert>
            </Card>
        );
    }

    if (loading) {
        return (
            <Center py="xl">
                <Loader />
            </Center>
        );
    }

    return (
        <Card shadow="sm" padding="sm" radius="lg" withBorder>
            <Stack gap="lg">
                <Group justify="space-between" align="center" wrap="wrap">
                    <Title order={2}>{t('users.title')}</Title>
                    {user?.user_type === 'MODERATOR' && (
                        <Group gap="xs" wrap="wrap">
                            <Button size="xs" variant="light" onClick={handleExportCsv} loading={exporting}>
                                {t('users.export.csv')}
                            </Button>
                            <Button size="xs" variant="light" onClick={handleExportXlsx} loading={exporting}>
                                {t('users.export.xlsx')}
                            </Button>
                        </Group>
                    )}
                </Group>
                <Text c="dimmed">{t('users.subtitle')}</Text>

                {error && !editingUser && (
                    <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" radius="md">
                        {error}
                    </Alert>
                )}

                <TextInput
                    label={t('users.search.label')}
                    placeholder={t('users.search.placeholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                    <Select
                        label={t('users.filters.role')}
                        value={roleFilter}
                        onChange={(value) => value && setRoleFilter(value)}
                        data={[
                            { value: 'ALL', label: t('users.filters.allRoles') },
                            { value: 'JOB_SEEKER', label: t('common.roles.jobSeeker') },
                            { value: 'JOB_POSTER', label: t('common.roles.jobPoster') },
                            { value: 'MODERATOR', label: t('common.roles.moderator') },
                            { value: 'ADMIN', label: t('common.roles.admin') },
                        ]}
                    />
                    <Select
                        label={t('users.filters.approval')}
                        value={approvalFilter}
                        onChange={(value) => value && setApprovalFilter(value)}
                        data={[
                            { value: 'ALL', label: t('users.filters.allApprovals') },
                            { value: 'APPROVED', label: t('users.filters.approved') },
                            { value: 'PENDING', label: t('users.filters.pending') },
                            { value: 'REJECTED', label: t('users.filters.rejected') },
                        ]}
                    />
                </SimpleGrid>

                {filteredUsers.length === 0 ? (
                    <Text c="dimmed">{t('users.noUsers')}</Text>
                ) : (
                    <div style={{ overflowX: 'auto', marginLeft: -32, marginRight: -32, paddingLeft: 32, paddingRight: 32 }}>
                        <Table striped highlightOnHover style={{ minWidth: 1200 }}>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>{t('users.table.email')}</Table.Th>
                                    <Table.Th>{t('users.table.phone')}</Table.Th>
                                    <Table.Th>{t('users.fields.firstName')}</Table.Th>
                                    <Table.Th>{t('users.fields.lastName')}</Table.Th>
                                    <Table.Th>{t('users.export.activity6m')}</Table.Th>
                                    <Table.Th>{t('users.export.activityTotal')}</Table.Th>
                                    <Table.Th>{t('approvals.career')}</Table.Th>
                                    <Table.Th style={{ minWidth: 120 }}>{t('users.table.role')}</Table.Th>
                                    <Table.Th style={{ minWidth: 120 }}>{t('users.table.approved')}</Table.Th>
                                    <Table.Th style={{ minWidth: 96 }}>{t('users.table.actions')}</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {pagedUsers.map((record) => (
                                    <Table.Tr key={record.id}>
                                        <Table.Td>{record.email}</Table.Td>
                                        <Table.Td>{record.phone_number || '—'}</Table.Td>
                                        <Table.Td>{record.first_name || '—'}</Table.Td>
                                        <Table.Td>{record.last_name || '—'}</Table.Td>
                                        <Table.Td>
                                            {record.user_type === 'JOB_SEEKER' && (
                                                <Text size="sm" c="dimmed">
                                                    {t('users.activity.applications6m', {
                                                        count: record.applications_last_6m ?? 0,
                                                    })}
                                                </Text>
                                            )}
                                            {record.user_type === 'JOB_POSTER' && (
                                                <Text size="sm" c="dimmed">
                                                    {t('users.activity.jobs6m', {
                                                        count: record.posted_jobs_last_6m ?? 0,
                                                    })}
                                                </Text>
                                            )}
                                            {!['JOB_SEEKER', 'JOB_POSTER'].includes(record.user_type) && '—'}
                                        </Table.Td>
                                        <Table.Td>
                                            {record.user_type === 'JOB_SEEKER' && (
                                                <Text size="sm">
                                                    {t('users.activity.applicationsTotal', {
                                                        count: record.applications_total ?? 0,
                                                    })}
                                                </Text>
                                            )}
                                            {record.user_type === 'JOB_POSTER' && (
                                                <Text size="sm">
                                                    {t('users.activity.jobsTotal', {
                                                        count: record.posted_jobs_total ?? 0,
                                                    })}
                                                </Text>
                                            )}
                                            {!['JOB_SEEKER', 'JOB_POSTER'].includes(record.user_type) && '—'}
                                        </Table.Td>
                                        <Table.Td>{getUserExtra(record)}</Table.Td>
                                        <Table.Td>
                                            <Badge variant="light" color="plum">
                                                {formatRole(record.user_type)}
                                            </Badge>
                                        </Table.Td>
                                        <Table.Td>
                                            <Badge
                                                color={
                                                    record.is_approved
                                                        ? 'green'
                                                        : record.rejected_at
                                                            ? 'red'
                                                            : 'gray'
                                                }
                                                variant="light"
                                            >
                                                {formatStatus(record)}
                                            </Badge>
                                        </Table.Td>
                                        <Table.Td>
                                            <Button size="xs" variant="light" onClick={() => openEdit(record)}>
                                                {t('users.table.edit')}
                                            </Button>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </div>
                )}

                {filteredUsers.length > pageSize && (
                    <Group justify="center" mt="sm">
                        <Pagination value={page} onChange={setPage} total={totalPages} />
                    </Group>
                )}
            </Stack>

            <Modal
                opened={Boolean(editingUser)}
                onClose={closeEdit}
                title={t('users.editTitle')}
                size="lg"
            >
                <Stack gap="md">
                    {error && (
                        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" radius="md">
                            {error}
                        </Alert>
                    )}
                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                        <TextInput
                            label={t('users.fields.email')}
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                        />
                        {editingUser?.user_type === 'ADMIN' ? (
                            <TextInput
                                label={t('users.fields.userType')}
                                value={t('common.roles.admin')}
                                disabled
                            />
                        ) : (
                            <Select
                                label={t('users.fields.userType')}
                                value={form.user_type}
                                onChange={(value) => value && setForm({ ...form, user_type: value })}
                                data={[
                                    { value: 'JOB_SEEKER', label: t('common.roles.jobSeeker') },
                                    { value: 'JOB_POSTER', label: t('common.roles.jobPoster') },
                                    { value: 'MODERATOR', label: t('common.roles.moderator') },
                                ]}
                            />
                        )}
                        <TextInput
                            label={t('users.fields.firstName')}
                            value={form.first_name}
                            onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                        />
                        <TextInput
                            label={t('users.fields.lastName')}
                            value={form.last_name}
                            onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                        />
                        <TextInput
                            label={t('users.fields.dni')}
                            value={form.dni}
                            onChange={(e) => setForm({ ...form, dni: e.target.value })}
                        />
                        <TextInput
                            label={t('users.fields.phone')}
                            value={form.phone_number}
                            onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                        />
                        {form.user_type === 'JOB_POSTER' && (
                            <TextInput
                                label={t('users.fields.companyName')}
                                value={form.company_name}
                                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                            />
                        )}
                        {form.user_type === 'JOB_POSTER' && (
                            <TextInput
                                label={t('users.fields.companyWebsite')}
                                value={form.company_website}
                                onChange={(e) => setForm({ ...form, company_website: e.target.value })}
                            />
                        )}
                        <Switch
                            label={t('users.fields.approved')}
                            checked={form.is_approved}
                            onChange={(e) => setForm({ ...form, is_approved: e.currentTarget.checked })}
                        />
                    </SimpleGrid>
                    <Textarea
                        label={t('users.fields.bio')}
                        value={form.bio}
                        onChange={(e) => setForm({ ...form, bio: e.target.value })}
                        minRows={3}
                    />
                    <TextInput
                        label={t('users.fields.skills')}
                        value={form.skills}
                        onChange={(e) => setForm({ ...form, skills: e.target.value })}
                        description={t('users.fields.skillsHint')}
                    />
                    {editingUser?.profile_picture && (
                        <Group gap="xs">
                            <Text fw={500}>{t('users.fields.profilePicture')}</Text>
                            <Anchor href={editingUser.profile_picture} target="_blank" rel="noopener noreferrer">
                                {t('users.fields.viewFile')}
                            </Anchor>
                        </Group>
                    )}
                    {editingUser?.resume && editingUser.user_type === 'JOB_SEEKER' && (
                        <Group gap="xs">
                            <Text fw={500}>{t('users.fields.resume')}</Text>
                            <Anchor href={editingUser.resume} target="_blank" rel="noopener noreferrer">
                                {t('users.fields.viewFile')}
                            </Anchor>
                        </Group>
                    )}
                    {editingUser?.constancia_document && editingUser.user_type === 'JOB_SEEKER' && (
                        <Group gap="xs">
                            <Text fw={500}>{t('users.fields.constancia')}</Text>
                            <Anchor href={editingUser.constancia_document} target="_blank" rel="noopener noreferrer">
                                {t('users.fields.viewFile')}
                            </Anchor>
                        </Group>
                    )}
                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                        <PasswordInput
                            label={t('users.password.new')}
                            value={resetPassword}
                            onChange={(e) => setResetPassword(e.target.value)}
                        />
                        <PasswordInput
                            label={t('users.password.confirm')}
                            value={resetPasswordConfirm}
                            onChange={(e) => setResetPasswordConfirm(e.target.value)}
                        />
                    </SimpleGrid>
                    <Group justify="flex-end" gap="sm">
                        <Button variant="subtle" onClick={closeEdit} disabled={saving}>
                            {t('users.cancel')}
                        </Button>
                        <Button onClick={handleSave} loading={saving}>
                            {t('users.save')}
                        </Button>
                    </Group>
                    {success && (
                        <Text size="sm" c="green" ta="right">
                            {t('users.updated')}
                        </Text>
                    )}
                </Stack>
            </Modal>
        </Card>
    );
};

export default UsersPage;
